import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Checkbox, MenuItem, Select } from "@mui/material"
import Card from "@mui/material/Card"

import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import { useLoading } from "../../LoadingContext"
import { useConnectionError } from "../../ConnectionErrorContext"
import {
  getSensitivitySettingList,
  SensitivitySettingListItem,
  SensitivitySettingUpdateItem,
  updateSensitivitySetting
} from "../../../api/sensitivity_setting"
import SensitivityHistoryModal from "../../components/SensitivityHistoryModal"
import DashboardLayout from "pages/components/DashboardLayout"
import ManageSensitivitySettingUpdate, { SensitivitySettingEditFormData } from "./update"
import DashboardNavbar from "pages/components/DashboardNavbar"
import Pagination from "../../Paginator"
import { sensitivityLabel, toSensitivityOption } from "../../../constants/sensitivity"

type SortKey =
  | "estate_name"
  | "facility_name"
  | "room_no"
  | "name"
  | "effective_sensitivity"
  | "effective_detection_period"
  | "effective_option_period"
  | "effective_sensitivity_source"

type SortDirection = "asc" | "desc"
type PeriodValue = string[] | null | undefined

const emptyEditFormData: SensitivitySettingEditFormData = {
  sensitivityEnabled: false,
  sensitivity: "96",
  detectionEnabled: false,
  detectionPeriodStart: "00:00",
  detectionPeriodEnd: "00:00",
  optionEnabled: false,
  optionPeriodStart: "00:00",
  optionPeriodEnd: "00:00"
}

const splitPeriod = (period?: PeriodValue | string) => {
  if (!period) {
    return { start: "00:00", end: "00:00" }
  }

  if (Array.isArray(period)) {
    return {
      start: period[0] || "00:00",
      end: period[1] || "00:00"
    }
  }

  if (typeof period === "string" && period.includes("-")) {
    const parts = period.split("-", 2)
    return {
      start: parts[0] || "00:00",
      end: parts[1] || "00:00"
    }
  }

  return { start: "00:00", end: "00:00" }
}

const formatSource = (source?: string | null) => {
  switch (source) {
    case "medical_record":
      return "入居者個別"
    case "catv":
      return "サービス提供元"
    case "system":
      return "システム設定"
    default:
      return "未設定"
  }
}

const formatPeriod = (period?: PeriodValue | string) => {
  if (!period) return "未設定"

  if (Array.isArray(period)) {
    if (period.length >= 2 && period[0] && period[1]) {
      return `${period[0]}〜${period[1]}`
    }
    return "未設定"
  }

  if (typeof period === "string" && period !== "") {
    return period.replace("-", "〜")
  }

  return "未設定"
}

const formatValue = (value?: string | number | string[] | null) => {
  if (Array.isArray(value)) {
    return formatPeriod(value)
  }
  if (value === undefined || value === null || value === "") {
    return "未設定"
  }
  return String(value)
}

const getSortableValue = (value: unknown) => {
  if (Array.isArray(value)) {
    return value.join("〜")
  }
  if (value === undefined || value === null) {
    return ""
  }
  return String(value)
}

const getSensitivitySortValue = (value: unknown) => {
  if (value === undefined || value === null || value === "") {
    return null
  }

  const numericValue = Number(value)
  return Number.isNaN(numericValue) ? null : numericValue
}

const buildInitialEditFormData = (item: SensitivitySettingListItem): SensitivitySettingEditFormData => {
  const detectionPeriod = splitPeriod(item.detection_period || item.effective_detection_period)
  const optionPeriod = splitPeriod(item.option_period || item.effective_option_period)

  return {
    sensitivityEnabled: item.sensitivity_enabled === 1,
    sensitivity:
      item.sensitivity !== undefined && item.sensitivity !== null
        ? String(toSensitivityOption(item.sensitivity))
        : item.effective_sensitivity !== undefined && item.effective_sensitivity !== null
          ? String(toSensitivityOption(item.effective_sensitivity))
          : "96",
    detectionEnabled: item.detection_enabled === 1,
    detectionPeriodStart: detectionPeriod.start,
    detectionPeriodEnd: detectionPeriod.end,
    optionEnabled: item.option_enabled === 1,
    optionPeriodStart: optionPeriod.start,
    optionPeriodEnd: optionPeriod.end
  }
}

const buildUpdateItem = (
  medicalRecordId: string,
  formData: SensitivitySettingEditFormData
): SensitivitySettingUpdateItem => {
  return {
    medical_record_id: medicalRecordId,

    // チェックON: 入居者個別設定として有効
    // チェックOFF: 個別設定なしに戻し、サービス提供元設定を継承
    sensitivity_enabled: formData.sensitivityEnabled ? 1 : null,
    sensitivity: formData.sensitivityEnabled ? Number(formData.sensitivity) : null,

    detection_enabled: formData.detectionEnabled ? 1 : null,
    detection_period_start: formData.detectionEnabled ? formData.detectionPeriodStart : null,
    detection_period_end: formData.detectionEnabled ? formData.detectionPeriodEnd : null,

    option_enabled: formData.optionEnabled ? 1 : null,
    option_period_start: formData.optionEnabled ? formData.optionPeriodStart : null,
    option_period_end: formData.optionEnabled ? formData.optionPeriodEnd : null
  }
}

const ManageSensitivitySetting: React.FC = () => {
  const navigate = useNavigate()
  const { startLoading, stopLoading } = useLoading()
  const { showConnectionError } = useConnectionError()

  const [items, setItems] = useState<SensitivitySettingListItem[]>([])
  const [selectedFacilityId, setSelectedFacilityId] = useState<string>("")
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [sortKey, setSortKey] = useState<SortKey>("facility_name")
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")
  const [editTarget, setEditTarget] = useState<SensitivitySettingListItem | null>(null)
  const [historyTarget, setHistoryTarget] = useState<SensitivitySettingListItem | null>(null)
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const [serverMessage, setServerMessage] = useState("")
  const [warningMessage, setWarningMessage] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  const loadList = async (facilityId?: string) => {
    try {
      startLoading()
      setServerMessage("")
      setWarningMessage("")

      const response = await getSensitivitySettingList(facilityId || null)
      if (response.success === 1) {
        setItems(response.items || [])
      } else if (response.success === 99) {
        navigate("/login")
      } else {
        setServerMessage(response.message || "感度設定一覧の取得に失敗しました")
      }
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login")
      } else {
        showConnectionError(true)
      }
    } finally {
      stopLoading()
    }
  }

  useEffect(() => {
    loadList()
  }, [])

  const facilityOptions = useMemo(() => {
    const map = new Map<string, string>()

    items.forEach((item) => {
      if (item.facility_id) {
        const estateName = item.estate_name || "不動産業者未設定"
        const facilityName = item.facility_name || item.facility_id
        map.set(item.facility_id, `${estateName} - ${facilityName}`)
      }
    })

    return Array.from(map.entries()).map(([facilityId, facilityName]) => ({
      facilityId,
      facilityName
    }))
  }, [items])

  const filteredItems = useMemo(() => {
    const filtered = selectedFacilityId ? items.filter((item) => item.facility_id === selectedFacilityId) : items
    return [...filtered].sort((a, b) => {
      let compare: number

      if (sortKey === "effective_sensitivity") {
        const valueA = getSensitivitySortValue(a.effective_sensitivity)
        const valueB = getSensitivitySortValue(b.effective_sensitivity)

        if (valueA === null && valueB === null) {
          compare = 0
        } else if (valueA === null) {
          compare = 1
        } else if (valueB === null) {
          compare = -1
        } else {
          compare = valueA - valueB
        }
      } else {
        const valueA = getSortableValue(a[sortKey])
        const valueB = getSortableValue(b[sortKey])
        compare = valueA.localeCompare(valueB, "ja")
      }

      return sortDirection === "asc" ? compare : -compare
    })
  }, [items, selectedFacilityId, sortKey, sortDirection])

  const paginatedItems = useMemo(() => {
    return filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
  }, [filteredItems, currentPage])

  const totalPages = Math.ceil(filteredItems.length / itemsPerPage)

  const changeSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDirection("asc")
    }

    setCurrentPage(1)
  }

  const sortHeaders: Array<{
    key: SortKey
    label: string
    width: string
    align?: "left" | "center" | "right"
  }> = [
    { key: "estate_name", label: "不動産業者", width: "120px", align: "left" },
    { key: "facility_name", label: "施設", width: "120px", align: "left" },
    { key: "room_no", label: "部屋", width: "70px", align: "center" },
    { key: "name", label: "入居者", width: "120px", align: "left" },
    { key: "effective_sensitivity", label: "感度", width: "70px", align: "center" },
    { key: "effective_detection_period", label: "検知時間帯", width: "120px", align: "center" },
    { key: "effective_option_period", label: "検知時間帯（オプション）", width: "160px", align: "center" },
    { key: "effective_sensitivity_source", label: "適用元", width: "120px", align: "center" }
  ]

  const getSortIcon = (key: SortKey) => {
    if (sortKey !== key) {
      return "▽"
    }

    return sortDirection === "asc" ? "▲" : "▼"
  }

  const handleSelect = (medicalRecordId: string, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(medicalRecordId) ? prev : [...prev, medicalRecordId]
      }
      return prev.filter((id) => id !== medicalRecordId)
    })
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filteredItems.map((item) => item.medical_record_id))
    } else {
      setSelectedIds([])
    }
  }

  const showWarnings = (response: any) => {
    if (response.sensing_server_warnings && response.sensing_server_warnings.length > 0) {
      setWarningMessage("DB更新は完了しましたが、一部のセンシングサーバー反映に失敗しました。")
    } else {
      setWarningMessage("")
    }
  }

  const handleSingleSubmit = async (formData: SensitivitySettingEditFormData) => {
    if (!editTarget) return

    const response = await updateSensitivitySetting([buildUpdateItem(editTarget.medical_record_id, formData)])
    if (response.success === 1 || response.success === 2) {
      setServerMessage(response.message || "感度設定を更新しました")
      showWarnings(response)
    }
    return response
  }

  const handleBulkSubmit = async (formData: SensitivitySettingEditFormData) => {
    const updateItems = selectedIds.map((medicalRecordId) => buildUpdateItem(medicalRecordId, formData))
    const response = await updateSensitivitySetting(updateItems)
    if (response.success === 1 || response.success === 2) {
      setServerMessage(response.message || "感度設定を更新しました")
      showWarnings(response)
    }
    return response
  }

  const thBaseStyle: React.CSSProperties = {
    padding: "8px",
    borderBottom: "1px solid #ddd",
    fontWeight: 700,
    fontSize: "0.78rem",
    color: "#344767",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    lineHeight: 1.4
  }

  const tdBaseStyle: React.CSSProperties = {
    padding: "8px",
    borderBottom: "1px solid #eee",
    fontSize: "0.78rem",
    color: "#344767",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    lineHeight: 1.4
  }

  const sortableHeaderStyle: React.CSSProperties = {
    ...thBaseStyle,
    cursor: "pointer",
    userSelect: "none"
  }

  const checkboxCellStyle: React.CSSProperties = {
    width: "48px",
    minWidth: "48px",
    maxWidth: "48px",
    padding: "8px 0 8px 8px",
    borderBottom: "1px solid #ddd",
    textAlign: "left",
    verticalAlign: "middle"
  }

  const checkboxBodyCellStyle: React.CSSProperties = {
    width: "48px",
    minWidth: "48px",
    maxWidth: "48px",
    padding: "8px 0 8px 8px",
    borderBottom: "1px solid #eee",
    textAlign: "left",
    verticalAlign: "middle"
  }

  const checkboxSx = {
    padding: 0,
    margin: 0
  }

  return (
    <DashboardLayout>
      <MDBox mb={4}>
        <DashboardNavbar absolute={true} />
      </MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" pt={3}>
        <MDBox>
          <MDTypography variant="h4" gutterBottom>
            感度設定
          </MDTypography>
        </MDBox>
      </MDBox>
      {/* 検索フォームとボタンエリア */}
      <MDBox
        mb={2}
        display="flex"
        justifyContent="space-between" // 左右端に配置
        alignItems="center"
        flexWrap="wrap"
        sx={{ fontSize: "0.875rem", color: "text.primary" }}
      >
        <MDBox>
          <MDBox mt={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
            <Select
              value={selectedFacilityId}
              displayEmpty
              onChange={(e) => {
                const facilityId = e.target.value
                setSelectedFacilityId(facilityId)
                setSelectedIds([])
                setCurrentPage(1)
              }}
              sx={{ minWidth: "240px", height: "42px" }}
            >
              <MenuItem value="">全施設</MenuItem>
              {facilityOptions.map((facility) => (
                <MenuItem key={facility.facilityId} value={facility.facilityId}>
                  {facility.facilityName}
                </MenuItem>
              ))}
            </Select>
            <MDButton variant="gradient" color="info" onClick={() => loadList()}>
              再読込
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              disabled={selectedIds.length === 0}
              onClick={() => setBulkEditOpen(true)}
            >
              選択した入居者を一括編集
            </MDButton>
          </MDBox>

          {serverMessage && (
            <MDBox mt={2} color="#d32f2f" fontSize="0.85rem">
              {serverMessage}
            </MDBox>
          )}
          {warningMessage && (
            <MDBox mt={1} color="#ed6c02" fontSize="0.85rem">
              {warningMessage}
            </MDBox>
          )}
        </MDBox>
      </MDBox>
      <MDBox>
        <Card>
          <MDBox
            mt={3}
            sx={{
              overflowX: "auto",
              overflowY: "auto",
              maxHeight: "calc(100vh - 300px)"
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
              <thead>
                <tr>
                  <th
                    style={{
                      ...checkboxCellStyle,
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    <Checkbox
                      sx={checkboxSx}
                      checked={filteredItems.length > 0 && selectedIds.length === filteredItems.length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </th>
                  <th
                    onClick={() => changeSort("estate_name")}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    不動産業者 {getSortIcon("estate_name")}
                  </th>

                  <th
                    onClick={() => changeSort("facility_name")}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    施設 {getSortIcon("facility_name")}
                  </th>

                  <th
                    onClick={() => changeSort("room_no")}
                    style={{
                      paddingRight: "8px",
                      paddingTop: "8px",
                      paddingBottom: "8px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    部屋 {getSortIcon("room_no")}
                  </th>

                  <th
                    onClick={() => changeSort("name")}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    入居者 {getSortIcon("name")}
                  </th>

                  <th
                    onClick={() => changeSort("effective_sensitivity")}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    感度 {getSortIcon("effective_sensitivity")}
                  </th>

                  <th
                    onClick={() => changeSort("effective_detection_period")}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    検知時間帯 {getSortIcon("effective_detection_period")}
                  </th>

                  <th
                    onClick={() => changeSort("effective_option_period")}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    検知時間帯（オプション） {getSortIcon("effective_option_period")}
                  </th>

                  <th
                    onClick={() => changeSort("effective_sensitivity_source")}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    適用元 {getSortIcon("effective_sensitivity_source")}
                  </th>
                  <th
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #ddd",
                      position: "sticky",
                      top: 0,
                      backgroundColor: "#fff",
                      zIndex: 1,
                      userSelect: "none"
                    }}
                  >
                    操作
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedItems.map((item) => (
                  <tr key={item.medical_record_id}>
                    <td style={checkboxBodyCellStyle}>
                      <Checkbox
                        sx={checkboxSx}
                        checked={selectedIds.includes(item.medical_record_id)}
                        onChange={(e) => handleSelect(item.medical_record_id, e.target.checked)}
                      />
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>{formatValue(item.estate_name)}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                      {formatValue(item.facility_name)}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>{formatValue(item.room_no)}</td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                      {formatValue(item.name || item.initial)}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                      {sensitivityLabel(item.effective_sensitivity)}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                      {formatPeriod(item.effective_detection_period)}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                      {formatPeriod(item.effective_option_period)}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                      {formatSource(item.effective_sensitivity_source)}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #eee" }}>
                      <MDBox display="flex" gap={1}>
                        <MDButton variant="gradient" color="info" size="small" onClick={() => setEditTarget(item)}>
                          編集
                        </MDButton>
                        <MDButton
                          variant="outlined"
                          color="info"
                          size="small"
                          onClick={() =>
                            navigate(
                              `/tenant/${item.facility_id}/${item.medical_record_uuid || item.medical_record_id}`
                            )
                          }
                        >
                          詳細
                        </MDButton>
                        <MDButton variant="outlined" color="info" size="small" onClick={() => setHistoryTarget(item)}>
                          履歴
                        </MDButton>
                      </MDBox>
                    </td>
                  </tr>
                ))}
                {paginatedItems.length === 0 && (
                  <tr>
                    <td colSpan={10} style={{ padding: "16px", textAlign: "center" }}>
                      表示対象の入居者がありません。
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </MDBox>
        </Card>
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

        {editTarget && (
          <ManageSensitivitySettingUpdate
            title="感度設定を編集"
            targetLabel={`${editTarget.facility_name || ""} ${editTarget.room_no || ""} ${
              editTarget.name || editTarget.initial || ""
            }`}
            initialData={buildInitialEditFormData(editTarget)}
            submitLabel="更新"
            onSubmit={handleSingleSubmit}
            onClose={async (updated) => {
              setEditTarget(null)
              if (updated) {
                await loadList()
              }
            }}
          />
        )}

        {bulkEditOpen && (
          <ManageSensitivitySettingUpdate
            title="感度設定を一括編集"
            targetLabel={`選択中の入居者 ${selectedIds.length} 件に同じ設定を反映します。`}
            initialData={emptyEditFormData}
            submitLabel="一括更新"
            onSubmit={handleBulkSubmit}
            onClose={async (updated) => {
              setBulkEditOpen(false)
              if (updated) {
                setSelectedIds([])
                await loadList()
              }
            }}
          />
        )}

        {historyTarget && (
          <SensitivityHistoryModal
            medicalRecordId={historyTarget.medical_record_id}
            title={`${historyTarget.facility_name || ""} ${historyTarget.room_no || ""} ${
              historyTarget.name || historyTarget.initial || ""
            } 感度変更履歴`}
            onClose={() => setHistoryTarget(null)}
          />
        )}
      </MDBox>
    </DashboardLayout>
  )
}

export default ManageSensitivitySetting
